import { Injectable } from '@angular/core';
import { AppKit, createAppKit } from '@reown/appkit';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { bsc, bscTestnet, mainnet, polygon, polygonMumbai, sepolia } from '@reown/appkit/networks';
import { ethers } from 'ethers';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { environment } from 'src/environments/environment';
import { Address } from 'viem';
import { AccountApi } from '../../apis/account.api';
import { Web3WalletSignatureInfo, Web3WalletType } from '../../interfaces/auth.interface';
import { NetworksInterface } from '../../interfaces/deposit.interface';
import { Web3WalletAdapter } from '../../interfaces/web3-wallet.interface';
import { LocalStorageService } from '../localstorage.service';
import { ToastService } from '../toast.service';

@Injectable({
  providedIn: 'root',
})
export class WalletConnectService implements Web3WalletAdapter {
  private modal: AppKit;
  private eip155Provider: any = null;

  // 支援的網路列表
  // PROD; mainnet, bsc, polygon
  // TEST: sepolia, bscTestnet, polygonMumbai
  // private supportedNetworks = [mainnet, bsc, polygon, sepolia, bscTestnet, polygonMumbai];
  private supportedNetworks = environment.isOnline ? [mainnet, bsc, polygon] : [sepolia, bscTestnet, polygonMumbai];

  constructor(
    private appService: AppService,
    private toastService: ToastService,
    private accountApi: AccountApi,
    private localStorageService: LocalStorageService,
  ) {
    /**
     * iOS + Chrome 深鏈接兼容性處理
     */
    // 方法二：直接覆寫 CoreHelperUtil.openHref。目前先採取window.open監聽方法
    // Hack: 強制使用 window.location.assign 打開深連結，避免 window.open 被彈窗阻擋
    // 並避免 window.open 產生的空白分頁 (僅對 deeplink 套用)
    // Ref: appkit-scaffold-ui/dist/esm/src/partials/w3m-connecting-wc-mobile/index.js
    // try {
    //   const originalOpenHref = CoreHelperUtil.openHref;
    //   CoreHelperUtil.openHref = (href: string, _target?: string, features?: string) => {
    //     if (!href) return;

    //     const isDeepLink = !/^https?:\/\//i.test(href);
    //     if (isDeepLink) {
    //       try {
    //         // 直接在當前分頁導向 deeplink，避免被視為彈窗
    //         window.location.assign(href);
    //         return;
    //       } catch (err) {
    //         try {
    //           // Safari 有時候 assign 也會被擋，改用 href 嘗試一次
    //           window.location.href = href;
    //           return;
    //         } catch (innerErr) {
    //           console.error('[WC][openHref deep link fallback error]', innerErr);
    //           // 最後才落回原本行為
    //         }
    //       }
    //     }

    //     originalOpenHref?.(href, _target as any, features);
    //   };
    // } catch (err) {
    //   console.error('[WC][patch openHref error]', err);
    // }

    // Initialize WalletConnect AppKit
    if (!window.WalletConnectInstance) {
      // 1. Get a project ID at https://cloud.reown.com
      // const projectId = '367c3a90e29497fcaba8d088677fc1a4'; // TEST Project ID
      const projectId = '32bea11719e76b76fc2233773893b933'; // Project ID

      // 2. Configure the metadata
      const metadata = {
        name: 'PUBS.com',
        description: 'PUBS.com',
        url: window.location.origin, // origin must match your domain & subdomain
        icons: [`${window.location.origin}/assets/resources/web3-wallets/dapp-logo.png`],
      };

      // 3. Create the modal
      const walletAppKit = createAppKit({
        projectId,
        metadata,
        adapters: [new EthersAdapter()],
        networks: [mainnet, bsc, polygon, sepolia, bscTestnet, polygonMumbai],
        coinbasePreference: 'eoaOnly',
        enableNetworkSwitch: false, // modal 左上角的網路
        enableReconnect: true, // 是否允許重新連接
        enableWalletGuide: false,
        allowUnsupportedChain: false,
        defaultAccountTypes: {
          eip155: 'eoa',
        },
        features: {
          analytics: true, // Optional - defaults to your Cloud configuration
          connectMethodsOrder: ['wallet'],
          swaps: false,
          onramp: false,
          send: false,
          allWallets: false,
          history: false,
          receive: false,
        },
        themeVariables: {
          '--w3m-z-index': 1001,
        },
        featuredWalletIds: [
          '8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4', // Binance Wallet
        ],
        excludeWalletIds: [
          // Wallet ID: https://docs.reown.com/cloud/wallets/wallet-list
          // 'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
          'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa', // Coinbase Wallet
        ],
      });

      // 4. Assign to global window object
      window.WalletConnectInstance = walletAppKit;
    }
    this.modal = window.WalletConnectInstance;

    // 設定監聽事件
    this._setupStateWatchers();

    // 檢查初始連接狀態
    this.checkExistingConnection();
  }

  private readonly _isConnected = new BehaviorSubject<boolean>(false);
  private readonly _accounts = new BehaviorSubject<string[]>([]);
  private readonly _currentChainId = new BehaviorSubject<number | string | null>(null);

  public readonly isConnected$: Observable<boolean> = this._isConnected.asObservable();
  public readonly accounts$: Observable<string[]> = this._accounts.asObservable();
  public readonly currentChainId$: Observable<number | string | null> = this._currentChainId.asObservable();

  /**
   * 設定錢包狀態監聽
   */
  private _setupStateWatchers() {
    this.appService.themeSwitch$.subscribe(theme => {
      this.modal.setThemeMode(theme === 'sun' ? 'light' : 'dark');
    });

    this.modal.subscribeProviders(providers => {
      this.eip155Provider = providers['eip155'];
    });

    this.modal.subscribeAccount(async account => {
      const currentAccount = this._accounts.getValue()[0];
      const newAddress = account.address;

      if (currentAccount) {
        // 判斷當已經有帳號，且新帳號的地址不同時
        if (newAddress !== currentAccount) {
          const isLogin = Boolean(this.localStorageService.loginToken);
          if (isLogin) {
            // 登入後，切換帳號時，直接登出
            this.appService.logoutSubject$.next(true);
          } else {
            // 未登入前，切換帳號時，直接斷線和reload
            await this.disconnectWallet();
            window.location.reload();
          }
        }
      } else {
        this._accounts.next(newAddress ? [newAddress] : []);

        // 登入後，如果地址抓不到，重新嘗試手動連線
        if (account.status === 'connected' && !newAddress && account.allAccounts.length === 1) {
          // window.location.reload();
          const curAccount = account.allAccounts[0];
          this.modal.setCaipAddress(
            `${curAccount.namespace}:${this.modal.getChainId()}:${curAccount.address}`,
            curAccount.namespace,
          );
        }
      }
    });

    this.modal.subscribeNetwork(network => {
      const currentChainId = this._currentChainId.getValue();
      const newChainId = network?.chainId ?? null;

      // 如果已經有chainId，且新 chainId 不同，則更新
      if (currentChainId !== newChainId) {
        this._currentChainId.next(newChainId);
      }
    });
  }

  /**
   * 檢查內部連接狀態
   */
  private _checkConnection(): void {
    if (!this._isConnected.getValue() || !this.modal || this._accounts.getValue().length === 0) {
      throw new Error('WalletConnect is not connected.');
    }
  }

  async checkExistingConnection(): Promise<boolean> {
    const isLogin = Boolean(this.localStorageService.loginToken);
    if (!isLogin || !this.modal) return false;

    await this.modal.ready();

    try {
      const existingAccount = this.modal.getAccount()?.address;
      if (existingAccount) {
        this._accounts.next([existingAccount]);

        // Reload後，判斷已連線的web3錢包是否為綁定於DB的web3錢包
        const web3WalletRes = await firstValueFrom(this.accountApi.getWeb3Wallet());
        const connectedWallets = web3WalletRes.data || [];
        const isExistingWallet = connectedWallets.some(
          wallet => wallet.address.toLowerCase() === existingAccount.toLowerCase(),
        );

        this.updateConnectedStatus(isExistingWallet);

        return isExistingWallet;
      }
    } catch (error) {
      console.error('Error checking existing connection in service:', error);
    }

    return false;
  }

  async connectWallet(): Promise<void> {
    await this.modal.ready();

    return new Promise((resolve, reject) => {
      if (!this.modal) {
        reject(new Error('WalletConnect AppKit is not initialized.'));
      }

      this.modal
        .open()
        .then(() => {
          // 已連結錢包，但未簽署時，直接返回
          if (this.modal.getAccount()?.address) {
            resolve();
          }

          // 監聽 walletConnect 彈窗事件
          const unSubscribeEvents = this.modal.subscribeEvents(async e => {
            const event = e.data.event;

            // 關閉時，取消訂閱
            if (event === 'MODAL_CLOSE') {
              if (this.modal.getAccount()?.address) {
                unSubscribeEvents();
                resolve();
              } else {
                await this.disconnectWallet();
                unSubscribeEvents();
                reject();
              }
            }
          });
        })
        .catch(err => {
          console.error('Failed to open wallet modal:', err);
          reject(err);
        });
    });
  }

  async disconnectWallet(): Promise<void> {
    await this.modal.disconnect();

    // Reset state
    this._isConnected.next(false);
    this._accounts.next([]);
    this._currentChainId.next(null);
    window.WalletConnectInstance = undefined;
  }

  updateConnectedStatus(isConnected: boolean) {
    this._isConnected.next(isConnected);
  }

  async signMessage(message: string): Promise<Web3WalletSignatureInfo | null> {
    const chainId = this._currentChainId.getValue();
    const signerAddress = this._accounts.getValue()[0];
    const hexMessage = '0x' + Buffer.from(message, 'utf8').toString('hex');
    const signature = await this.eip155Provider.request({
      method: 'personal_sign',
      params: [hexMessage, signerAddress],
    });

    if (signature && chainId) {
      // 關閉 WalletConnect 彈窗
      await this.modal.close();

      return {
        walletType: Web3WalletType.WalletConnect,
        network: chainId.toString(),
        address: signerAddress,
        signature: signature,
      };
    }

    return null;
  }

  fetchCurrentChainId(): Promise<number | null> {
    return new Promise((resolve, reject) => {
      const chainId = this.modal.getChainId();

      if (chainId) {
        this._currentChainId.next(chainId);
        resolve(Number(chainId));
      } else {
        this._currentChainId.next(null);
        reject(null);
      }
    });
  }

  async fetchBalance(): Promise<string | null> {
    this._checkConnection();

    // 方法一：使用 ethers.js
    try {
      const address = this._accounts.getValue()[0]; // 獲取第一個帳戶地址
      const getProvider = this.modal.getWalletProvider() as any;

      // 用 SDK provider 初始化 ethers provider
      const ethersProvider = new ethers.BrowserProvider(getProvider);
      const balanceWei = await ethersProvider.getBalance(address);
      const formattedBalance = ethers.formatEther(balanceWei);

      return formattedBalance;
    } catch (err) {
      console.error('Fetch balance error:', err);
      return null;
    }

    // 方法二：walletConnect提供的方法
    // return new Promise((resolve, reject) => {
    //   const curAccount = this._accounts.getValue()[0];
    //   const curChainId = this.modal.getChainId();
    //   const curNetwork = this.modal.getCaipNetwork('eip155', curChainId);

    //   if (!curAccount || !curChainId || !curNetwork) {
    //     reject('Error fetching balance: No account connected');
    //     return;
    //   }

    //   this.modal.chainAdapters?.eip155
    //     .getBalance({ address: curAccount, chainId: curChainId, caipNetwork: curNetwork })
    //     .then(result => {
    //       resolve(result.balance.toString());
    //     })
    //     .catch(err => {
    //       console.error('Error fetching balance:', err);
    //       reject(err);
    //     });
    // });
  }

  async fetchContractBalance(network: NetworksInterface): Promise<string | null> {
    this._checkConnection();

    try {
      const address = this._accounts.getValue()[0];
      const getProvider = this.modal.getWalletProvider() as any;

      // 用 SDK provider 初始化 ethers provider
      const ethersProvider = new ethers.BrowserProvider(getProvider);
      const { contractAddress, contractABI, decimals } = network;
      const contract = new ethers.Contract(contractAddress, contractABI, ethersProvider);
      const curBalance = await contract
        .balanceOf(address)
        .then(data => {
          const balance = data / BigInt(10 ** decimals);
          const remainder = data % BigInt(10 ** decimals);
          const finalBalance = parseFloat(balance.toString() + '.' + remainder.toString().padStart(decimals, '0'));

          return finalBalance;
        })
        .catch(err => {
          console.error('Fetch contract balance error:', err);
          return null;
        });

      return curBalance?.toString() || '';
    } catch (err) {
      console.error('Fetch contract balance error:', err);
      return null;
    }
  }

  async sendMainTransaction(toAddress: string, amount: string): Promise<string | null> {
    this._checkConnection();

    const showErrorMsg = () => {
      this.toastService.show({
        title: 'Deposit request failed',
        message: 'You rejected the transaction request',
        type: 'fail',
      });
    };

    try {
      const getProvider = this.modal.getWalletProvider() as any;
      const curAddress = this._accounts.getValue()[0];
      const tx = {
        from: curAddress,
        to: toAddress,
        value: ethers.parseEther(amount),
      };

      // 用 SDK provider 初始化 ethers provider
      const ethersProvider = new ethers.BrowserProvider(getProvider);
      const signer = await ethersProvider.getSigner();
      const result = await signer.sendTransaction(tx).catch(err => {
        showErrorMsg();
        console.error('WalletConnect Error sending transaction:', err);
        return null;
      });

      return result?.hash || null;
    } catch (err) {
      showErrorMsg();
      console.error('WalletConnect Transaction failed:', err);
      return null;
    }
  }

  async sendContractTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    this._checkConnection();

    const { contractABI, decimals, contractAddress } = network;
    const curChainId = this.modal.getChainId();
    const curNetwork = this.modal.getCaipNetwork('eip155', curChainId);
    const curProvider = this.modal.getWalletProvider() as any;
    const curCaipAddress = this.modal.getCaipAddress('eip155');
    const curAddress = this._accounts.getValue()[0];

    if (!curNetwork || !curCaipAddress || !this.modal.chainAdapters?.eip155) {
      return null;
    }

    const showErrorMsg = () => {
      this.toastService.show({
        title: 'Deposit request failed',
        message: 'You rejected the transaction request',
        type: 'fail',
      });
    };

    try {
      // 使用 ethers.utils.parseUnits 來處理數量轉換
      // 它會回傳一個 BigNumber 物件，最後再轉成字串
      const amountToSend = ethers.parseUnits(amount, decimals).toString();

      const contractResult = await this.modal.chainAdapters?.eip155
        .writeContract({
          tokenAddress: contractAddress as Address, // Contract address
          fromAddress: curAddress as Address,
          method: 'transfer',
          abi: JSON.parse(contractABI),
          args: [toAddress, amountToSend], // transfer 函數的參數
          chainNamespace: 'eip155',
          caipNetwork: curNetwork,
          caipAddress: curCaipAddress,
          provider: curProvider,
        })
        .catch(err => {
          showErrorMsg();
          console.error('WalletConnect Error sending transaction:', err);
          return null;
        });

      const result = contractResult?.hash as any;
      return result?.hash || null;
    } catch (err) {
      showErrorMsg();
      console.error('WalletConnect Transaction failed:', err);
      return null;
    }
  }

  async switchNetwork(chainId: number | string | null): Promise<void> {
    const networkToSwitch = this.supportedNetworks.find(network => network.id === Number(chainId));

    const showErrorMsg = () => {
      this.toastService.show({
        title: 'Network not detected',
        message:
          'This network is not enabled in your wallet. Please enable it and try again or select a different network.',
        type: 'fail',
      });
    };

    if (!networkToSwitch) {
      showErrorMsg();
      console.error('Failed to switch network: network not found');
      throw new Error('Failed to switch network: network not found');
    }

    try {
      await this.modal.switchNetwork(networkToSwitch);

      // 方法二：
      // eip155 自動切換網路: wallet_switchEthereumChain
      // const getProvider = this.modal.getWalletProvider() as any;
      // const hexChainId = '0x' + chainId?.toString(16);
      // getProvider?.request({
      //   method: 'wallet_switchEthereumChain',
      //   params: [{ chainId: hexChainId }],
      // });
      // this._currentChainId.next(chainId);
    } catch (err) {
      showErrorMsg();
      console.error('Failed to switch network:', err);
      throw err;
    }
  }
}
